<script setup lang="ts">
import {
  isCollectiveAuthor,
  type BlogAuthorSummary,
} from "#shared/utils/blogAuthors";
import { useBlog } from "~/composables/useBlog";

/**
 * Page AUTEUR — /blog/auteur/younes-miloud
 *
 * POURQUOI : le blog fait 30 % des impressions Google de Donoor, et pourtant
 * aucune de ses pages ne disait QUI écrit. C'est le signal E-E-A-T le plus
 * visible qui manquait (Expérience, Expertise, Autorité, Fiabilité) — et c'est
 * exactement ce que les moteurs de réponse regardent pour décider qui citer.
 *
 * Elle rend deux choses que le seul article ne peut pas rendre :
 *   - une entité identifiable (Person ou Organization en JSON-LD) ;
 *   - la production complète de cet auteur, ce qui donne à Google un signal
 *     d'expertise sur un domaine plutôt qu'article par article.
 *
 * ⚠️ Person vs Organization : baliser « Équipe Donoor » comme une personne
 * serait FAUX, et un balisage faux est pire que pas de balisage (Google
 * dévalue alors toutes les données structurées de la page). D'où
 * `isCollectiveAuthor`.
 */
const route = useRoute();
const slug = (route.params as Record<string, string>).slug ?? "";

const { getImageUrl, formatDate } = useBlog();

const { data: authorData } = await useAsyncData(`blog-author-${slug}`, () =>
  $fetch<{ author: BlogAuthorSummary | null }>(
    `/api/blog/authors?slug=${encodeURIComponent(slug)}`,
  ).catch(() => ({ author: null })),
);

const author = computed(() => authorData.value?.author ?? null);
if (!author.value) {
  throw createError({ statusCode: 404, statusMessage: "Auteur introuvable" });
}

const { data: articlesData } = await useAsyncData(
  `blog-author-articles-${slug}`,
  () =>
    $fetch<{
      articles: Array<{
        id: string;
        title: string;
        slug: string;
        description?: string;
        image?: string;
        date_published: string;
      }>;
      total: number;
    }>(`/api/blog/articles?author=${author.value?.id}&limit=50`).catch(() => ({
      articles: [],
      total: 0,
    })),
);

const articles = computed(() => articlesData.value?.articles ?? []);

const canonicalUrl = computed(() => `https://donoor.org/blog/auteur/${slug}`);
const isCollective = computed(() => isCollectiveAuthor(author.value?.name ?? ""));

const seoTitle = computed(() =>
  author.value?.role
    ? `${author.value.name}, ${author.value.role} — Articles sur le blog Donoor`
    : `${author.value?.name} — Articles sur le blog Donoor`,
);
const seoDescription = computed(
  () =>
    author.value?.bio?.slice(0, 155) ||
    `Les ${articles.value.length} articles publiés par ${author.value?.name} sur le blog Donoor : gestion d'association, collecte de dons, fiscalité.`,
);

// Une page auteur sans article n'apporte rien : même règle de substance que
// pour les hubs géographiques de l'annuaire.
const shouldNoindex = computed(() => articles.value.length === 0);
const robotsContent = useRobotsContent(shouldNoindex);

useSeoMeta({
  title: () => seoTitle.value,
  description: () => seoDescription.value,
  ogTitle: () => seoTitle.value,
  ogDescription: () => seoDescription.value,
  ogType: "profile",
  ogUrl: () => canonicalUrl.value,
  ogLocale: "fr_FR",
  robots: () => robotsContent.value,
});

useHead({
  link: computed(() => [
    { rel: "canonical", key: "canonical", href: canonicalUrl.value },
  ]),
  script: computed(() => [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "ProfilePage",
        url: canonicalUrl.value,
        mainEntity: {
          "@type": isCollective.value ? "Organization" : "Person",
          "@id": `${canonicalUrl.value}#author`,
          name: author.value?.name,
          ...(author.value?.role && !isCollective.value
            ? { jobTitle: author.value.role }
            : {}),
          ...(author.value?.bio ? { description: author.value.bio } : {}),
          ...(author.value?.avatar
            ? { image: getImageUrl(author.value.avatar, 512) }
            : {}),
          ...(isCollective.value ? {} : { worksFor: { "@type": "Organization", name: "Donoor", url: "https://donoor.org" } }),
        },
        breadcrumb: {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Donoor", item: "https://donoor.org" },
            { "@type": "ListItem", position: 2, name: "Blog", item: "https://donoor.org/blog" },
            { "@type": "ListItem", position: 3, name: author.value?.name, item: canonicalUrl.value },
          ],
        },
      }),
    },
  ]),
});
</script>

<template>
  <div v-if="author" class="min-h-screen bg-white">
    <div class="pt-32 sm:pt-36 pb-10 px-4 bg-[#fafcff] border-b border-gray-200">
      <UContainer>
        <nav class="text-sm text-gray-500 mb-4">
          <NuxtLink to="/blog" class="hover:underline">Blog</NuxtLink>
          <span class="mx-1.5">›</span>
          <span>{{ author.name }}</span>
        </nav>

        <div class="flex items-center gap-4">
          <NuxtImg
            v-if="author.avatar"
            :src="getImageUrl(author.avatar, 160)"
            :alt="author.name"
            class="size-16 sm:size-20 rounded-full object-cover"
            width="80"
            height="80"
          />
          <div>
            <h1 class="text-2xl sm:text-3xl font-bold text-gray-900">
              {{ author.name }}
            </h1>
            <p v-if="author.role" class="text-sm text-gray-600 mt-1">
              {{ author.role }} · Donoor
            </p>
          </div>
        </div>

        <p v-if="author.bio" class="mt-4 text-sm sm:text-base text-gray-600 max-w-2xl leading-relaxed">
          {{ author.bio }}
        </p>
      </UContainer>
    </div>

    <div class="px-4 py-10 sm:py-12">
      <UContainer>
        <h2 class="text-lg font-semibold text-gray-900 mb-5">
          {{ articles.length }} article{{ articles.length > 1 ? "s" : "" }} publié{{ articles.length > 1 ? "s" : "" }}
        </h2>

        <ul class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <li
            v-for="article in articles"
            :key="article.id"
            class="group rounded-2xl border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
          >
            <NuxtLink :to="`/blog/${article.slug}`" class="block">
              <NuxtImg
                :src="getImageUrl(article.image, 600)"
                :alt="article.title"
                class="h-40 w-full object-cover"
                loading="lazy"
                width="600"
                height="320"
              />
              <div class="p-4">
                <p class="text-xs text-gray-400 mb-1">
                  {{ formatDate(article.date_published) }}
                </p>
                <h3 class="text-base font-semibold text-gray-900 leading-snug group-hover:text-(--color-main)">
                  {{ article.title }}
                </h3>
              </div>
            </NuxtLink>
          </li>
        </ul>

        <p class="mt-10 text-sm text-gray-500">
          <NuxtLink to="/blog/archives/1" class="text-(--color-main) hover:underline">
            Voir tous les articles du blog
          </NuxtLink>
        </p>
      </UContainer>
    </div>
  </div>
</template>
